import { withHTMLComponent } from '@updater-legacy/components/custom-element';
import styles from './carousel.component.css';
import { touch } from './carousel.touch';
import { spring } from './carousel.spring';

const tpl = `<div id="track"><slot name="item"></slot></div>`;

const calculateCurrentSlideIndex = (
  slideWidth,
  slideCount,
  prevSlideIndex,
  startPosition,
  endPosition
) => {
  const start = startPosition.x;
  const end = endPosition.x;
  const diff = Math.abs(end - start);
  let slideIndex = prevSlideIndex;
  const swipeThreshold = 24;

  if (diff > swipeThreshold) {
    const additional = Math.floor((diff - swipeThreshold) / slideWidth);

    if (end < start) {
      slideIndex = prevSlideIndex + 1 + additional;
    } else {
      slideIndex = prevSlideIndex - 1 - additional;
    }
  }

  if (slideIndex < 0) slideIndex = 0;
  if (slideIndex > slideCount - 1) {
    slideIndex = slideCount - 1;
  }

  return slideIndex;
};

const calculateTargetPosition = (viewWidth, slideWidth, index) => {
  const edge = viewWidth > slideWidth ? (viewWidth - slideWidth) / 2 : 0;
  return { x: -index * slideWidth + edge };
};

const clickEngine = (element, { onClick }) => {
  element.addEventListener('click', onClick);

  return {
    destroy() {
      element.removeEventListener('click', onClick);
    },
  };
};

class Carousel extends withHTMLComponent(styles, tpl)() {
  static props = ['slideCount', 'slideWidth', 'scrollable', 'trackWidth'];

  constructor() {
    super();

    this.track = this.shadowRoot.querySelector('#track');
  }

  connected() {
    this._reset();
  }

  disconnected() {
    this.springEngine && this.springEngine.destroy();
    this.touchEngine && this.touchEngine.destroy();
  }

  get slides() {
    return [].slice.call(this.children);
  }

  _reset = () => {
    this.touchEngine && this.touchEngine.destroy();
    this.springEngine && this.springEngine.destroy();
    this.clickEngine && this.clickEngine.destroy();

    // Update Layout

    !this.scrollable && (this.track.style.transform = '');
    this.track.style.width = `${this.trackWidth}px`;

    if (!this.scrollable) return;

    // Touch Engine

    let prevPosition = { x: 0 };
    let prevSlideIndex = 0;

    this.touchEngine = touch(this.track, {
      onTouchMove: (start, end) => {
        const deltaX = end.x - start.x;
        const x = prevPosition.x + deltaX;
        this.springEngine.target(x);
      },
      onTouchEnd: (start, end) => {
        const targetSlideIndex = calculateCurrentSlideIndex(
          this.slideWidth,
          this.slideCount,
          prevSlideIndex,
          start,
          end
        );

        const position = calculateTargetPosition(
          this.clientWidth,
          this.slideWidth,
          targetSlideIndex
        );

        this.springEngine.target(position.x);

        if (prevSlideIndex !== targetSlideIndex) {
          this._handleChange(targetSlideIndex);
        }

        prevSlideIndex = targetSlideIndex;
        prevPosition = position;
      },
    });

    // Click Engine

    this.clickEngine = clickEngine(this.shadowRoot, {
      onClick: (e) => {
        const targetSlide = e.target.closest('[slot]');
        const targetIndex = this.slides.findIndex(
          (item) => item === targetSlide
        );
        const targetPosition = calculateTargetPosition(
          this.clientWidth,
          this.slideWidth,
          targetIndex
        );
        this.springEngine.target(targetPosition.x);

        if (prevSlideIndex !== targetIndex) {
          this._handleChange(targetIndex);
        }

        prevPosition = targetPosition;
        prevSlideIndex = targetIndex;
      },
    });

    // Spring Engine

    const position = calculateTargetPosition(
      this.clientWidth,
      this.slideWidth,
      prevSlideIndex
    );
    this.track.style.transform = `translateX(${position.x}px)`;
    prevPosition = position;

    this.springEngine = spring({
      initialTarget: position.x,
      callbackFn: (x) => (this.track.style.transform = `translateX(${x}px)`),
    });
  };

  updated({ slideWidth, slideCount, trackWidth, scrollable }) {
    Object.assign(this, { slideWidth, slideCount, trackWidth, scrollable });
    this._reset();
  }

  _handleChange(index) {
    this.dispatchEvent(new CustomEvent('change', { detail: { index } }));
  }
}

customElements.define('upd-carousel', Carousel);
